import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Icon, { IconSizes } from '../../../components/foundations/Icon';
import { COLUMNS, NEW_RULE_ID, TRANSLATION_KEY } from '../constants';
import { filterRules, isEditing, placeDuplicateRow } from '../utils';
import { ActionsShape, EditingShape, RuleShape } from '../propShapes';
import RuleRow from './RuleRow';
import RuleEditor from './RuleEditor';
import css from '../style.css';

const sortIconName = (column, sortKey, sortDir) => {
    if (sortKey !== column.key) return 'arrows-sort';
    return sortDir === 'asc' ? 'sort-ascending' : 'sort-descending';
};

const RulesTable = ({
    rules,
    editing,
    searchText,
    sortKey,
    sortDir,
    rejectSaveDisabled,
    saving,
    translate,
    actions,
}) => {
    const visibleRules = placeDuplicateRow(filterRules(rules, searchText));
    const count = visibleRules.length;

    return (
        <div>
            <div className={css.rulesCount}>
                {translate(`${TRANSLATION_KEY}.RULES_AVAILABLE.${count === 1 ? 'ONE' : 'OTHER'}`, { count })}
                {searchText ? ` ${translate(`${TRANSLATION_KEY}.FILTERED`)}` : ''}
            </div>
            <table className={css.fraudTable}>
                <colgroup>
                    {COLUMNS.map(column => (
                        <col key={column.key} style={{ width: column.width }} />
                    ))}
                </colgroup>
                <thead>
                    <tr>
                        {COLUMNS.map(column => (
                            <th key={column.key} onClick={() => actions.onSort(column)}>
                                <span className={css.headerLabel}>
                                    {translate(column.labelKey)}
                                    <Icon
                                        className={classNames(css.sortIcon, {
                                            [css.sortActive]: sortKey === column.key,
                                        })}
                                        name={sortIconName(column, sortKey, sortDir)}
                                        size={IconSizes.X_SMALL}
                                    />
                                </span>
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {visibleRules.map(rule => {
                        const open = isEditing(editing, rule.id);
                        const draft = open ? editing[rule.id] : rule;
                        return (
                            <React.Fragment key={rule.id}>
                                <RuleRow
                                    rule={draft}
                                    editing={open}
                                    searchText={searchText}
                                    translate={translate}
                                    editRule={actions.editRule}
                                    onClick={() => rule.id > NEW_RULE_ID && actions.toggleEdit(rule.id)}
                                />
                                {open && (
                                    <RuleEditor
                                        draft={draft}
                                        savedRule={rules.find(r => r.id === rule.id)}
                                        conditionOptions={actions.conditionOptions}
                                        rejectSaveDisabled={rejectSaveDisabled}
                                        saving={saving}
                                        translate={translate}
                                        editRule={actions.editRule}
                                        addCondition={actions.addCondition}
                                        editCondition={actions.editCondition}
                                        removeCondition={actions.removeCondition}
                                        save={actions.save}
                                        cancelEdit={actions.cancelEdit}
                                        deleteRule={actions.deleteRule}
                                        duplicate={actions.duplicate}
                                    />
                                )}
                            </React.Fragment>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

RulesTable.propTypes = {
    rules: PropTypes.arrayOf(RuleShape).isRequired,
    editing: EditingShape.isRequired,
    searchText: PropTypes.string,
    sortKey: PropTypes.string,
    sortDir: PropTypes.string,
    rejectSaveDisabled: PropTypes.bool,
    saving: PropTypes.bool,
    translate: PropTypes.func.isRequired,
    actions: ActionsShape.isRequired,
};

export default RulesTable;
